import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Search, AlertTriangle, MessageSquare, Euro, CheckCircle, XCircle, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  'Ouvert': 'bg-red-100 text-red-700',
  'En cours': 'bg-orange-100 text-orange-700',
  'Résolu': 'bg-emerald-100 text-emerald-700',
  'Rejeté': 'bg-gray-100 text-gray-600',
};

const statusIcons = {
  'Ouvert': AlertTriangle,
  'En cours': Clock,
  'Résolu': CheckCircle,
  'Rejeté': XCircle,
};

const typeColors = {
  'Problème de commande': 'bg-blue-100 text-blue-700',
  'Problème de paiement': 'bg-purple-100 text-purple-700',
  'Problème de qualité': 'bg-orange-100 text-orange-700',
  'Problème de retrait': 'bg-yellow-100 text-yellow-700',
  'Autre': 'bg-gray-100 text-gray-600',
};

export default function AdminDisputes() {
  useAuth();
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchDisputes = useCallback(async () => {
    try {
      let url = `${API}/admin/disputes`;
      if (statusFilter !== 'all') {
        url += `?status=${encodeURIComponent(statusFilter)}`;
      }
      const response = await axios.get(url);
      setDisputes(response.data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/disputes/stats`);
      setStats(response.data);
    } catch {
      console.error('Error fetching stats');
    }
  }, []);

  useEffect(() => {
    fetchDisputes();
    fetchStats();
  }, [fetchDisputes, fetchStats]);

  const openDetailModal = async (dispute) => {
    try {
      const response = await axios.get(`${API}/admin/disputes/${dispute.id}`);
      setSelectedDispute(response.data);
      setAdminResponse(response.data.admin_response || '');
      setRefundAmount(response.data.refund_amount?.toString() || '');
      setNewStatus(response.data.status);
      setShowDetailModal(true);
    } catch {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleUpdateDispute = async () => {
    if (!selectedDispute) return;
    setUpdating(true);
    
    try {
      const updateData = {
        status: newStatus,
        admin_response: adminResponse || null,
        refund_amount: refundAmount ? parseFloat(refundAmount) : null
      };
      
      await axios.put(`${API}/admin/disputes/${selectedDispute.id}`, updateData);
      
      toast.success('Litige mis à jour');
      setShowDetailModal(false);
      fetchDisputes();
      fetchStats();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const filteredDisputes = disputes.filter(d => 
    d.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.restaurant_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout title="Gestion des litiges">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-gray-500">Ouverts</span>
            </div>
            <span className="text-2xl font-bold text-red-600">{stats.open}</span>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-gray-500">En cours</span>
            </div>
            <span className="text-2xl font-bold text-orange-600">{stats.in_progress}</span>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-gray-500">Résolus</span>
            </div>
            <span className="text-2xl font-bold text-emerald-600">{stats.resolved}</span>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-500">Rejetés</span>
            </div>
            <span className="text-2xl font-bold text-gray-600">{stats.rejected}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un litige..."
            className="pl-12 rounded-xl"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 rounded-xl">
            <SelectValue placeholder="Tous les statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="Ouvert">Ouvert</SelectItem>
            <SelectItem value="En cours">En cours</SelectItem>
            <SelectItem value="Résolu">Résolu</SelectItem>
            <SelectItem value="Rejeté">Rejeté</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Disputes List */}
      <div className="space-y-4">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : filteredDisputes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucun litige trouvé</p>
          </div>
        ) : (
          filteredDisputes.map((dispute) => {
            const StatusIcon = statusIcons[dispute.status] || AlertTriangle;
            return (
              <div 
                key={dispute.id} 
                className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openDetailModal(dispute)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <StatusIcon className={`w-5 h-5 ${dispute.status === 'Ouvert' ? 'text-red-500' : dispute.status === 'En cours' ? 'text-orange-500' : 'text-gray-500'}`} />
                      <span className="font-semibold text-gray-900">Commande {dispute.order_number}</span>
                      <Badge className={statusColors[dispute.status]}>{dispute.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-2">
                      <span>Client: {dispute.client_name}</span>
                      <span>•</span>
                      <span>Restaurant: {dispute.restaurant_name}</span>
                    </div>
                    <Badge className={typeColors[dispute.type]}>{dispute.type}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {new Date(dispute.created_at).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="font-semibold text-gray-900">{dispute.order_amount?.toFixed(2)} €</p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-gray-600 line-clamp-2">{dispute.description}</p>
              </div>
            );
          })
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails du litige</DialogTitle>
          </DialogHeader>
          
          {selectedDispute && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Informations de la commande</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Numéro:</span>
                    <p className="font-medium">{selectedDispute.order?.order_number}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Montant:</span>
                    <p className="font-medium">{selectedDispute.order?.total_amount?.toFixed(2)} €</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Date:</span>
                    <p className="font-medium">
                      {selectedDispute.order?.created_at && new Date(selectedDispute.order.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Statut commande:</span>
                    <p className="font-medium">{selectedDispute.order?.status}</p>
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div className="bg-blue-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Client</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Nom:</span>
                    <p className="font-medium">{selectedDispute.client?.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{selectedDispute.client?.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Téléphone:</span>
                    <p className="font-medium">{selectedDispute.client?.phone || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Dispute Details */}
              <div className="bg-orange-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Détails du litige</h3>
                <div className="mb-3">
                  <Badge className={typeColors[selectedDispute.type]}>{selectedDispute.type}</Badge>
                </div>
                <p className="text-gray-700">{selectedDispute.description}</p>
              </div>

              {/* Admin Response Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="w-full rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ouvert">Ouvert</SelectItem>
                      <SelectItem value="En cours">En cours</SelectItem>
                      <SelectItem value="Résolu">Résolu</SelectItem>
                      <SelectItem value="Rejeté">Rejeté</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MessageSquare className="w-4 h-4 inline mr-1" />
                    Réponse de l'admin
                  </label>
                  <Textarea
                    value={adminResponse}
                    onChange={(e) => setAdminResponse(e.target.value)}
                    placeholder="Votre réponse au client..."
                    rows={4}
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Euro className="w-4 h-4 inline mr-1" />
                    Montant du remboursement (optionnel)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder="0.00"
                    className="rounded-xl"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    className="flex-1 rounded-xl"
                    onClick={() => setShowDetailModal(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    className="flex-1 rounded-xl bg-emerald-500 hover:bg-emerald-600"
                    onClick={handleUpdateDispute}
                    disabled={updating}
                  >
                    {updating ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
